import { useEffect, useState } from "react";
import { api, formatDateTime, formatDate } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select } from "@/components/ui/Primitives";
import { KeyRound, Plus, Ban, Unlock, Activity } from "lucide-react";
import { toast } from "sonner";

const HOLDER_TYPES = ["tenant","applicant","contractor","cleaner"];
const STATUS_TONE = { active: "green", revoked: "red", expired: "slate" };

export default function AccessKeys() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    property_id: "", holder_type: "tenant", holder_email: "", holder_name: "",
    label: "", valid_from: "", valid_until: ""
  });

  const load = async () => {
    setLoading(true);
    try {
      const [k, p] = await Promise.all([
        api.get("/access-keys").catch(() => ({ data: [] })),
        api.get("/properties").catch(() => ({ data: [] })),
      ]);
      setItems(Array.isArray(k.data) ? k.data : (k.data?.keys || []));
      setProperties(Array.isArray(p.data) ? p.data : (p.data?.properties || []));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/access-keys", {
        property_id: form.property_id,
        holder_type: form.holder_type,
        holder_email: form.holder_email || null,
        holder_name: form.holder_name || null,
        label: form.label || null,
        valid_from: form.valid_from || new Date().toISOString(),
        valid_until: form.valid_until || new Date(Date.now() + 7 * 86400000).toISOString(),
      });
      toast.success("Access key created");
      setOpen(false);
      setForm({ property_id: "", holder_type: "tenant", holder_email: "", holder_name: "", label: "", valid_from: "", valid_until: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create access key");
    } finally { setSaving(false); }
  };

  const revoke = async (key) => {
    if (!confirm("Revoke this access key?")) return;
    try {
      await api.patch(`/access-keys/${key.key_id}/revoke`);
      toast.success("Access key revoked");
      load();
    } catch { toast.error("Could not revoke"); }
  };

  const simulateUnlock = async (key) => {
    try {
      await api.post(`/access-keys/${key.key_id}/unlock`);
      toast.success("Unlock recorded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unlock");
    }
  };

  const showLogs = async (key) => {
    try {
      const { data } = await api.get(`/access-keys/${key.key_id}/logs`);
      setLogs({ key, entries: Array.isArray(data) ? data : (data?.logs || []) });
    } catch { toast.error("Could not load logs"); }
  };

  if (loading) return <Loader/>;

  return (
    <div>
      <PageHeader
        title="Access keys"
        subtitle="Time-bound digital access for tenants, contractors, and applicants."
        actions={<Button onClick={() => setOpen(true)} disabled={properties.length === 0} data-testid="new-key-button"><Plus className="w-4 h-4"/> Issue key</Button>}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No access keys yet"
          description="Issue time-bound digital keys to anyone visiting the property — tenants, contractors, cleaners."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-3 font-semibold">Holder</th>
                  <th className="px-5 py-3 font-semibold">Property</th>
                  <th className="px-5 py-3 font-semibold">Valid until</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((k) => {
                  const prop = properties.find((p) => p.property_id === k.property_id);
                  return (
                    <tr key={k.key_id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`key-row-${k.key_id}`}>
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{k.holder_name || k.holder_email || "—"}</div>
                        <div className="text-xs text-slate-500 capitalize mt-0.5">{k.holder_type} · {k.label || "—"}</div>
                      </td>
                      <td className="px-5 py-4 text-slate-700">{prop?.name || k.property_id?.slice(-6)}</td>
                      <td className="px-5 py-4 text-slate-700">{formatDateTime(k.valid_until)}</td>
                      <td className="px-5 py-4"><Pill tone={STATUS_TONE[k.status] || "slate"}>{k.status}</Pill></td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button onClick={() => showLogs(k)} className="p-2 rounded hover:bg-slate-100 text-slate-600" title="Audit logs" data-testid={`logs-${k.key_id}`}>
                          <Activity className="w-4 h-4"/>
                        </button>
                        <button onClick={() => simulateUnlock(k)} className="p-2 rounded hover:bg-slate-100 text-slate-600" title="Simulate unlock" data-testid={`unlock-${k.key_id}`}>
                          <Unlock className="w-4 h-4"/>
                        </button>
                        {k.status === "active" && (
                          <button onClick={() => revoke(k)} className="p-2 rounded hover:bg-red-50 text-red-600" title="Revoke" data-testid={`revoke-${k.key_id}`}>
                            <Ban className="w-4 h-4"/>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Issue access key" testid="key-modal">
        <form onSubmit={submit} className="space-y-4">
          <Select label="Property" required value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})}>
            <option value="">Select property</option>
            {properties.map((p) => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
          </Select>
          <Select label="Holder type" value={form.holder_type} onChange={(e)=>setForm({...form, holder_type:e.target.value})}>
            {HOLDER_TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Holder name" value={form.holder_name} onChange={(e)=>setForm({...form, holder_name:e.target.value})}/>
            <Input label="Holder email" type="email" value={form.holder_email} onChange={(e)=>setForm({...form, holder_email:e.target.value})}/>
          </div>
          <Input label="Label / reason" placeholder="Move-in, repair visit, etc." value={form.label} onChange={(e)=>setForm({...form, label:e.target.value})}/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Valid from" type="datetime-local" value={form.valid_from} onChange={(e)=>setForm({...form, valid_from:e.target.value})}/>
            <Input label="Valid until" type="datetime-local" value={form.valid_until} onChange={(e)=>setForm({...form, valid_until:e.target.value})}/>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="key-create-submit">{saving ? "Issuing…" : "Issue key"}</Button>
          </div>
        </form>
      </Modal>

      <Modal open={!!logs} onClose={() => setLogs(null)} title="Access audit log" testid="key-logs-modal">
        {logs && (
          <div>
            <div className="text-sm text-slate-600 mb-3">Activity for <b>{logs.key.holder_name || logs.key.holder_email}</b></div>
            {logs.entries.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-500">No access events yet.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {logs.entries.map((e, i) => (
                  <div key={i} className="py-3 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-semibold text-slate-900">{e.action || e.event_type || "Unlock"}</div>
                      <div className="text-xs text-slate-500">{e.ip_address || e.method || "—"}</div>
                    </div>
                    <div className="text-xs text-slate-500">{formatDateTime(e.created_at || e.timestamp)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
